import * as XLSX from "xlsx";
import { buildExcelBlob } from "@/helpers/excel";
import { BatchColumn, BatchFieldError } from "@/components/batchPreview";

export const TEMPLATE_HEADERS = [
  "ID",
  "Cédula",
  "Nombre",
  "Correo",
  "Teléfono",
  "Concepto",
  "Fecha 1",
  "Monto 1",
  "Fecha 2",
  "Monto 2",
  "Fecha 3",
  "Monto 3",
  "Fecha 4",
  "Monto 4",
];

export type BatchRow = Record<string, string | number | undefined>;

const PAIRS = [1, 2, 3, 4];

const isBlank = (value: unknown) =>
  value === undefined || value === null || String(value).trim() === "";

const pad = (n: number) => String(n).padStart(2, "0");

const serialToDate = (serial: number) => {
  const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(serial) * 86_400_000);
  return `${pad(d.getUTCDate())}/${pad(
    d.getUTCMonth() + 1
  )}/${d.getUTCFullYear()}`;
};

export const readInvoiceFile = async (
  file: File
): Promise<{ rows: BatchRow[]; hasContrato: boolean } | null> => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const ref = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");
  const [header = []] = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    range: { s: ref.s, e: { c: ref.e.c, r: ref.s.r } },
  });
  const headers = header.map((h) => String(h ?? "").trim());
  if (!headers.includes("ID") || !headers.includes("Cédula")) return null;

  const rows = XLSX.utils
    .sheet_to_json<Record<string, unknown>>(sheet)
    .map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [key.trim(), value])
      )
    )
    .filter((row) => !Object.values(row).every(isBlank))
    .map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => [
          key,
          !/^Fecha \d$/.test(key)
            ? (value as string | number)
            : typeof value === "number"
            ? serialToDate(value)
            : normalizeCell(key, String(value)),
        ])
      )
    );

  return { rows, hasContrato: headers.includes("Contrato") };
};

const toIsoDate = (value: unknown) => {
  const match = String(value)
    .trim()
    .match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
  return parsed.getDate() === Number(day) && parsed.getMonth() + 1 === +month
    ? `${year}-${month}-${day}`
    : undefined;
};

const amountError = (value: unknown) => {
  const amount = Number(value);
  if (amount < 3000) return "Monto inválido";
  if (amount > 550_000_000) return "Máximo $550.000.000";
  if (!Number.isInteger(amount)) return "Debe ser un número entero";
};

export const validateRow = (
  row: BatchRow,
  today: string
): BatchFieldError[] => {
  const errors: BatchFieldError[] = [];
  const error = (field: string, message: string) =>
    errors.push({ field, message });

  if (isBlank(row["ID"])) error("ID", "Falta el ID");
  if (isBlank(row["Cédula"])) error("Cédula", "Falta la cédula");
  if (isBlank(row["Concepto"])) error("Concepto", "Falta el concepto");
  if (
    !isBlank(row["Correo"]) &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(row["Correo"]).trim())
  )
    error("Correo", "Correo inválido");

  const dates: { field: string; iso: string }[] = [];
  let pairErrors = 0;
  for (const i of PAIRS) {
    const dateValue = row[`Fecha ${i}`];
    const amountValue = row[`Monto ${i}`];
    if (isBlank(dateValue) && isBlank(amountValue)) continue;
    const iso = toIsoDate(dateValue);
    const message = isBlank(dateValue)
      ? ["Fecha", "Falta la fecha"]
      : isBlank(amountValue) || Number(amountValue) === 0
      ? ["Monto", "Falta el monto"]
      : !iso
      ? ["Fecha", "Fecha inválida, usa DD/MM/AAAA"]
      : amountError(amountValue) && ["Monto", amountError(amountValue)!];
    if (message) {
      pairErrors += 1;
      error(`${message[0]} ${i}`, message[1]);
    } else dates.push({ field: `Fecha ${i}`, iso: iso! });
  }

  if (!dates.length && !pairErrors) error("Fecha 1", "Falta fecha y monto");
  const last = dates.sort((a, b) => a.iso.localeCompare(b.iso)).pop();
  if (!pairErrors && last && last.iso < today)
    error(last.field, "Fecha ya pasó");

  return errors;
};

const cop = new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 });

export const formatCop = (value: unknown) =>
  typeof value === "number" ? `$ ${cop.format(value)}` : String(value ?? "");

export const invoiceColumns = (
  rows: BatchRow[],
  hasContrato: boolean
): BatchColumn[] => {
  const lastPair = Math.max(
    1,
    ...PAIRS.filter((i) =>
      rows.some(
        (row) => !isBlank(row[`Fecha ${i}`]) || !isBlank(row[`Monto ${i}`])
      )
    )
  );
  const suffix = (i: number) => (lastPair > 1 ? ` ${i}` : "");

  return [
    { key: "ID", label: "ID", minWidth: "120px" },
    { key: "Cédula", label: "Cédula", minWidth: "120px", inputMode: "numeric" },
    { key: "Nombre", label: "Inquilino", minWidth: "160px" },
    { key: "Correo", label: "Correo", minWidth: "180px", inputMode: "email" },
    { key: "Teléfono", label: "WhatsApp", minWidth: "130px", inputMode: "tel" },
    { key: "Concepto", label: "Concepto", minWidth: "160px" },
    ...(hasContrato ? [{ key: "Contrato", label: "Contrato" }] : []),
    ...PAIRS.slice(0, lastPair).flatMap((i): BatchColumn[] => [
      { key: `Fecha ${i}`, label: `Vence${suffix(i)}`, minWidth: "120px" },
      {
        key: `Monto ${i}`,
        label: `Valor${suffix(i)}`,
        minWidth: "130px",
        align: "end",
        inputMode: "numeric",
        format: formatCop,
      },
    ]),
  ];
};

export const normalizeCell = (
  key: string,
  raw: string
): string | number | undefined => {
  const value = raw.trim();
  if (!value) return undefined;

  if (/^Monto \d$/.test(key)) {
    const cleaned = value
      .replace(/[$\s]/g, "")
      .replace(/[.,](?=\d{3}(?!\d))/g, "")
      .replace(",", ".");
    return /^\d+(\.\d+)?$/.test(cleaned) ? Number(cleaned) : value;
  }

  if (/^Fecha \d$/.test(key)) {
    const dmy = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmy) return `${pad(+dmy[1])}/${pad(+dmy[2])}/${dmy[3]}`;
    const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
  }

  return value;
};

export const duplicateIds = (rows: BatchRow[]): Set<number> => {
  const seen = new Set<string>();
  const duplicates = new Set<number>();
  rows.forEach((row, index) => {
    const id = String(row["ID"] ?? "").trim();
    if (!id) return;
    if (seen.has(id)) duplicates.add(index);
    seen.add(id);
  });
  return duplicates;
};

export const buildInvoiceFile = (
  rows: BatchRow[],
  hasContrato: boolean,
  fileName: string
): File => {
  const headers = [...TEMPLATE_HEADERS, ...(hasContrato ? ["Contrato"] : [])];
  const blob = buildExcelBlob([
    headers,
    ...rows.map((row) =>
      headers.map((header) =>
        isBlank(row[header]) ? undefined : (row[header] as string | number)
      )
    ),
  ]);
  return new File([blob], fileName.replace(/\.[^.]+$/, "") + ".xlsx", {
    type: blob.type,
  });
};
